import { Component, Inject, OnInit } from '@angular/core'
import { CommonModule } from '@angular/common'
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog'
import { PipesModule } from '@app/shared/pipe/index.module'
import { SharedModule } from '@app/shared/shared.module'
import { ExportDocumentService } from '@app/features/scope-overview/service/export-document.service'
import { DialogEventsService } from '@app/features/scope-overview/service/dialog-events.service'
import { ExportDocusignModal } from '../export-docusign/export-docusign.component'
import { ExportModalConfig } from '@app/features/scope-overview/model/export-modal-config'
import { ExportAdobsignModal } from '@app/features/scope-overview/components/export-adobesign/export-adobesign.component'
import { AuthService } from '@core/service/auth.service'
import { Privilege } from '@app/core/model/enums/privilege.enum'
import { SNACKBAR_LENGTH_LONG, SnackbarEventType, SnackbarService } from '@shared/utils/snackbar.service'

@Component({
  selector: 'export-modal',
  standalone: true,
  imports: [CommonModule, PipesModule, MatDialogModule, SharedModule],
  templateUrl: './export-modal.component.html',
})
export class ExportModalComponent implements OnInit {
  hasDocusignPermission: boolean
  hasAdobeSignPermission: boolean
  ready = false

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ExportModalConfig,
    public dialogRef: MatDialogRef<ExportModalComponent>,
    public dialog: MatDialog,
    private dialogEventService: DialogEventsService,
    private exportDocumentService: ExportDocumentService,
    private authService: AuthService,
    private snackbarService: SnackbarService
  ) {
    this.hasDocusignPermission = this.exportDocumentService.hasDocusignPermission(
      this.data.currentScope,
      this.data.currentUser
    )
    this.hasAdobeSignPermission = this.exportDocumentService.hasAdobeSignPermission(
      this.data.currentScope,
      this.data.currentUser
    )
  }

  ngOnInit() {
    if (!this.data.skipWhenSingle) {
      this.ready = true
      return
    }

    const availableFormats = this.getAvailableFormats()
    if (availableFormats.length === 0) {
      this.snackbarService.showPrioritisedSnackbar(
        "You don't have the right permission for this",
        SNACKBAR_LENGTH_LONG,
        SnackbarEventType.ERROR
      )
      this.dialogRef.close()
      return
    }

    if (availableFormats.length === 1) {
      this.exportSingleFormat(availableFormats[0])
      return
    }

    this.ready = true
  }

  private getAvailableFormats(): string[] {
    const user = this.authService.loggedInUser
    const scope = this.data.currentScope
    const isByRole = scope?.isScopeByRole()
    const hasDocuSignPrivilege = user?.hasPrivilege(Privilege.SCOPE__EXPORT_DOCU_SIGN, scope) ?? false
    const formats: string[] = []

    if (user?.hasPrivilege(Privilege.SCOPE__EXPORT_CSV, scope)) {
      formats.push('CSV')
    }
    if (user?.hasPrivilege(Privilege.SCOPE__EXPORT_DOCX, scope)) {
      formats.push('WORD')
    }
    if (user?.hasPrivilege(Privilege.SCOPE__EXPORT_PDF, scope) && user?.hasPrivilege(Privilege.SCOPE__VIEW_RATES, scope)) {
      formats.push('PDF')
    }
    if (user?.hasPrivilege(Privilege.SCOPE__EXPORT_XLSX, scope)) {
      formats.push('EXCEL')
    }
    if (this.hasDocusignPermission && !isByRole && hasDocuSignPrivilege) {
      formats.push('DOCUSIGN')
    }
    if (this.hasAdobeSignPermission && !isByRole && hasDocuSignPrivilege) {
      formats.push('ADOBESIGN')
    }
    return formats
  }

  private exportSingleFormat(format: string) {
    const actions: { [key: string]: () => void } = {
      CSV: () => this.exportToCSV(),
      WORD: () => this.exportToWordDoc(),
      PDF: () => this.exportScopeToPdf(),
      EXCEL: () => this.exportScopeToExcel(),
      DOCUSIGN: () => this.showDocusignModal(),
      ADOBESIGN: () => this.showAdobesignModal(),
    }
    actions[format]?.()
  }

  exportToCSV() {
    this.exportDocumentService.exportToCSV(this.data.scopeId, this.data.currentScope.name)
    this.dialogRef.close()
  }

  exportToWordDoc() {
    this.exportDocumentService.exportToWordDoc(this.data.scopeId, this.data.currentScope.name)
    this.dialogRef.close()
  }

  // NOTE: Working but will need to be tested on deployed version.
  exportScopeToPdf() {
    this.exportDocumentService.exportToPdf(this.data.scopeId, this.data.currentScope.name)
    this.dialogRef.close()
  }

  exportScopeToExcel() {
    this.dialogEventService.emitEvent({ key: 'exportScopeToExcel' })
    this.dialogRef.close()
  }

  showDocusignModal() {
    this.dialogRef.close()
    const dialogConfig: ExportModalConfig = {
      title: 'Export As DocuSign Document',
      body: 'Select which output will be sent via DocuSign',
      isOutputOption: true,
      currentScope: this.data.currentScope,
      currentUser: this.data.currentUser,
      scopeId: this.data.scopeId,
    }

    this.dialog.open(ExportDocusignModal, { data: dialogConfig })
  }

  showAdobesignModal() {
    this.dialogRef.close()
    const dialogConfig: ExportModalConfig = {
      title: 'Export As AdobeSign Document',
      body: 'Select which output will be sent via AdobeSign',
      isOutputOption: true,
      currentScope: this.data.currentScope,
      currentUser: this.data.currentUser,
      scopeId: this.data.scopeId,
    }

    this.dialog.open(ExportAdobsignModal, { data: dialogConfig })
  }
}
